import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Youtube, FileText, ImageOff, Loader2 } from 'lucide-react';
import { API } from '../../lib/apiBase';

const YT_ID_RE = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

const extractYouTubeThumbnail = (url) => {
  if (!url) return '';
  const match = url.match(YT_ID_RE);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : '';
};

const empty = {
  title: '',
  url: '',
  type: 'youtube',
  author: '',
  description: '',
  brand: '',
  model: '',
  thumbnail_url: '',
};

const ReviewLinksManager = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [oembedLoading, setOembedLoading] = useState(false);
  const fetchedUrlRef = useRef('');

  // Live preview: manual thumbnail_url wins; otherwise auto-extract from YouTube URL
  const previewThumbnail = useMemo(() => {
    if (form.thumbnail_url) return form.thumbnail_url;
    if (form.type === 'youtube') return extractYouTubeThumbnail(form.url);
    return '';
  }, [form.thumbnail_url, form.type, form.url]);

  useEffect(() => { setPreviewError(false); }, [previewThumbnail]);

  // Auto-fill title + author from YouTube oEmbed (debounced)
  useEffect(() => {
    if (form.type !== 'youtube') return undefined;
    if (!YT_ID_RE.test(form.url)) return undefined;
    if (fetchedUrlRef.current === form.url) return undefined;

    const timer = setTimeout(async () => {
      setOembedLoading(true);
      try {
        const { data } = await axios.get(`${API}/youtube/oembed`, {
          params: { url: form.url },
          withCredentials: true,
        });
        fetchedUrlRef.current = form.url;
        setForm((prev) => {
          // Only fill if user hasn't typed in those fields yet
          if (prev.url !== form.url) return prev;
          return {
            ...prev,
            title: prev.title || data.title || '',
            author: prev.author || data.author_name || '',
          };
        });
      } catch (error) {
        // Silent fail - admin can still fill manually
        console.warn('oEmbed fetch failed:', error?.response?.data?.detail || error.message);
      } finally {
        setOembedLoading(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [form.url, form.type]);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/review-links`);
      setLinks(data);
    } catch (error) {
      console.error('Error loading links:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/review-links`, form, { withCredentials: true });
      setForm(empty);
      loadLinks();
    } catch (error) {
      alert(`Hata: ${error.response?.data?.detail || 'Bir hata oluştu'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu inceleme linkini silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/review-links/${id}`, { withCredentials: true });
      loadLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="border border-border bg-card p-6" data-testid="add-link-form-section">
        <h2 className="text-xl font-heading font-bold mb-6">Yeni İnceleme Linki Ekle</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Başlık *</Label>
            <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="rounded-sm" data-testid="link-title-input" />
          </div>

          <div>
            <Label htmlFor="url">URL *</Label>
            <div className="relative">
              <Input id="url" type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required className="rounded-sm font-mono text-sm pr-9" placeholder="https://..." data-testid="link-url-input" />
              {oembedLoading && (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" data-testid="oembed-loading" />
              )}
            </div>
            {form.type === 'youtube' && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="oembed-hint">
                YouTube linki için başlık ve kanal otomatik doldurulur.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tip *</Label>
              <Select value={form.type} onValueChange={(value) => setForm({ ...form, type: value })}>
                <SelectTrigger className="rounded-sm" data-testid="link-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="youtube">YouTube Video</SelectItem>
                  <SelectItem value="article">Makale / Yazı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="author">Yazar / Kanal *</Label>
              <Input id="author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} required className="rounded-sm" data-testid="link-author-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="brand">Marka</Label>
              <Input id="brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-sm" data-testid="link-brand-input" />
            </div>
            <div>
              <Label htmlFor="model">Model</Label>
              <Input id="model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-sm" data-testid="link-model-input" />
            </div>
          </div>

          <div>
            <Label htmlFor="thumbnail_url">Kapak Görseli URL'i</Label>
            <Input id="thumbnail_url" value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className="rounded-sm font-mono text-sm" placeholder="https://..." data-testid="link-thumbnail-input" />
            {form.type === 'youtube' && (
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="thumbnail-auto-hint">
                Boş bırakırsanız YouTube linkinden otomatik alınır.
              </p>
            )}

            {previewThumbnail && (
              <div className="mt-3" data-testid="thumbnail-preview-wrapper">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  Önizleme
                </p>
                <div className="relative w-full aspect-video border border-border bg-secondary overflow-hidden">
                  {previewError ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="thumbnail-preview-error">
                      <ImageOff className="w-8 h-8" />
                      <span className="text-xs">Görsel yüklenemedi</span>
                    </div>
                  ) : (
                    <img
                      src={previewThumbnail}
                      alt="Kapak önizlemesi"
                      className="w-full h-full object-cover"
                      onError={() => setPreviewError(true)}
                      data-testid="thumbnail-preview-img"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="description">Kısa Açıklama</Label>
            <Textarea id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm" data-testid="link-description-input" />
          </div>

          <Button type="submit" disabled={submitting} className="rounded-sm bg-primary" data-testid="link-submit-button">
            <Plus className="w-4 h-4 mr-2" />
            {submitting ? 'Ekleniyor...' : 'İncelemeyi Ekle'}
          </Button>
        </form>
      </div>

      <div className="border border-border bg-card p-6" data-testid="links-list-section">
        <h2 className="text-xl font-heading font-bold mb-6">Mevcut İncelemeler ({links.length})</h2>

        {loading ? (
          <p className="text-muted-foreground">Yükleniyor...</p>
        ) : links.length === 0 ? (
          <p className="text-muted-foreground">Henüz inceleme eklenmemiş.</p>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {links.map((link) => (
              <div key={link.id} className="border border-border p-4" data-testid={`link-item-${link.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {link.type === 'youtube' ? <Youtube className="w-4 h-4 text-destructive flex-shrink-0" /> : <FileText className="w-4 h-4 text-foreground flex-shrink-0" />}
                      <h3 className="font-semibold truncate">{link.title}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {link.author}
                      {(link.brand || link.model) && (<span className="ml-2 font-mono">· {link.brand} {link.model}</span>)}
                    </p>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline font-mono truncate block">{link.url}</a>
                  </div>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(link.id)} className="rounded-sm flex-shrink-0" data-testid={`delete-link-button-${link.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewLinksManager;
